import type { ShipmentRow } from '../../types';
import { ShipmentLegModeIcon } from '@/assets/icons/icons';

export type LegModeIcon = 'truck' | 'plane' | 'fallback';

export interface LegModeDisplay {
  label: string;
  icon: LegModeIcon;
}

const TRUCK_ICON_TOKENS = [
  'truck',
  'truckicon',
  'linehaul',
  'cartage',
  'clearance',
  'road',
  'ground',
];

const PLANE_ICON_TOKENS = ['airporticon', 'plane', 'fly', 'air', 'flight'];

const toNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const normalizeToken = (value: string): string =>
  value.toLowerCase().replace(/[\s_-]+/g, '');

const inferIconFromToken = (token?: string | null): LegModeIcon | null => {
  const normalized = toNonEmptyString(token);
  if (!normalized) return null;
  const key = normalizeToken(normalized);
  if (TRUCK_ICON_TOKENS.some((candidate) => key.includes(candidate))) {
    return 'truck';
  }
  if (PLANE_ICON_TOKENS.some((candidate) => key.includes(candidate))) {
    return 'plane';
  }
  return null;
};

const inferIconFromLabel = (label?: string | null): LegModeIcon => {
  const normalized = toNonEmptyString(label);
  if (!normalized) return 'fallback';
  const key = normalizeToken(normalized);
  if (
    key.includes('linehaul') ||
    key.includes('cartage') ||
    key.includes('clearance') ||
    key.includes('truck') ||
    key.includes('road') ||
    key.includes('ground')
  ) {
    return 'truck';
  }
  if (key.includes('air') || key.includes('plane') || key.includes('flight')) {
    return 'plane';
  }
  return 'fallback';
};

const isObjectRecord = (
  value: unknown
): value is Record<string, string | null | undefined> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const getLegModeDisplay = (
  record: ShipmentRow
): LegModeDisplay | null => {
  const legModeObject = isObjectRecord(record.leg_mode)
    ? record.leg_mode
    : null;
  const modeObject =
    legModeObject ?? (isObjectRecord(record.mode) ? record.mode : null);
  const label =
    toNonEmptyString(modeObject?.name) ??
    toNonEmptyString(modeObject?.value) ??
    toNonEmptyString(record.mode_name) ??
    toNonEmptyString(record.mode_label) ??
    toNonEmptyString(record.shipment_mode) ??
    toNonEmptyString(record.leg_mode) ??
    (typeof record.mode === 'string' ? toNonEmptyString(record.mode) : null);

  if (!label) return null;

  const token =
    toNonEmptyString(modeObject?.icon_token) ??
    toNonEmptyString(record.mode_icon_token);
  const icon = inferIconFromToken(token) ?? inferIconFromLabel(label);

  return { label, icon };
};

/**
 * Maps a resolved leg-mode label string to the corresponding ShipmentLegModeIcon
 * enum value. Shared by the shipment table reference cell and TrackingStatusCard.
 */

export const getLegModeIconType = (label: string): ShipmentLegModeIcon => {
  const mode = String(label ?? '').toLowerCase();
  if (mode.includes('air')) return ShipmentLegModeIcon.Air;
  if (mode.includes('linehaul') || mode.includes('line haul')) {
    return ShipmentLegModeIcon.Linehaul;
  }
  if (mode.includes('cartage')) return ShipmentLegModeIcon.Cartage;
  if (mode.includes('clearance') || mode.includes('custom')) {
    return ShipmentLegModeIcon.Clearance;
  }
  if (mode.includes('empty')) return ShipmentLegModeIcon.Empty;
  return ShipmentLegModeIcon.Unknown;
};
